import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function ModelFailoverContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">模型故障转移</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 的模型故障转移机制，确保在主模型不可用时自动切换到备用模型，保证服务连续性。
        </p>
      </div>

      <SectionTitle id="failover-auth">认证存储（密钥 + OAuth）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        故障转移系统管理多种认证方式：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
        {[
          { icon: 'ri-key-2-line', title: 'API 密钥', desc: '直接使用 API 密钥认证，存储在环境变量或配置文件中' },
          { icon: 'ri-shield-keyhole-line', title: 'OAuth 令牌', desc: '通过 OAuth 流程获取的访问令牌，存储在系统密钥链中' },
        ].map((item) => (
          <div key={item.title} className="flex items-start gap-3 bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className={`${item.icon} text-cyan-400 text-lg`}></i>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-1">{item.title}</div>
              <div className="text-xs text-slate-400">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="failover-profile">配置文件 ID</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个账户配置都有唯一的配置文件 ID，用于在故障转移规则中引用：
      </p>
      <CodeBlock language="json5" code={`{
  models: {
    anthropic: {
      // 配置文件 ID: "primary"
      primary: {
        apiKey: "YOUR_ANTHROPIC_API_KEY_PRIMARY",
        // 该配置文件的优先级（数字越小优先级越高）
        priority: 1,
      },
      // 配置文件 ID: "secondary"
      secondary: {
        apiKey: "YOUR_ANTHROPIC_API_KEY_SECONDARY",
        priority: 2,
      },
      // 配置文件 ID: "openrouter-fallback"
      "openrouter-fallback": {
        provider: "openrouter",
        apiKey: "YOUR_OPENROUTER_API_KEY",
        priority: 3,
      }
    }
  }
}`} />

      <SectionTitle id="failover-rotation">轮换顺序</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        故障转移按配置的优先级顺序轮换账户：
      </p>
      <div className="space-y-2 my-4">
        {[
          { label: '主账户 (priority: 1)', status: '正常使用', color: 'emerald' },
          { label: '次账户 (priority: 2)', status: '主账户失败时切换', color: 'amber' },
          { label: 'OpenRouter 备用 (priority: 3)', status: '前两个都失败时切换', color: 'rose' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0">{i + 1}</span>
            <div className="flex-1 flex items-center justify-between bg-slate-800/40 border border-slate-700/50 rounded-lg px-3 py-2">
              <span className="text-slate-300 text-sm">{item.label}</span>
              <span className={`text-xs text-${item.color}-400`}>{item.status}</span>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="failover-cooldown">冷却时间</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        账户失败后会进入冷却期，冷却期内不会再次尝试该账户：
      </p>
      <CodeBlock language="json5" code={`{
  models: {
    failover: {
      // 账户失败后的冷却时间（秒）
      cooldownSeconds: 300,
      // 冷却期结束后的探测策略
      // "immediate" - 立即尝试
      // "gradual" - 逐渐增加尝试频率
      recoveryStrategy: "gradual",
      // 连续失败多少次后进入冷却
      failureThreshold: 3,
    }
  }
}`} />

      <SectionTitle id="failover-billing">账单禁用</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        当账户因账单问题（余额不足、超出限额等）被禁用时，系统会自动识别并切换：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>HTTP 402 (Payment Required) 错误触发账单禁用检测</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>账单禁用的账户会被标记，冷却时间更长（默认 24 小时）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>可以配置账单禁用通知（邮件、Webhook 等）</span></li>
      </ul>
      <CodeBlock language="json5" code={`{
  models: {
    failover: {
      billing: {
        // 账单禁用后的冷却时间（秒）
        disabledCooldownSeconds: 86400,  // 24 小时
        // 是否发送通知
        notify: true,
        // 通知 Webhook URL
        notifyWebhook: "https://your-webhook.example.com/billing-alert",
      }
    }
  }
}`} />

      <SectionTitle id="failover-model-fallback">模型回退</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        除了账户级别的故障转移，还支持模型级别的回退：
      </p>
      <CodeBlock language="json5" code={`{
  models: {
    fallback: [
      {
        // 主模型
        primary: "claude-3-5-sonnet-20241022",
        // 回退模型列表（按顺序尝试）
        fallbacks: [
          "claude-3-haiku-20240307",
          "gpt-4o-mini",
          "gemini-1.5-flash",
        ]
      }
    ]
  }
}`} />
      <InfoBox type="tip">
        模型回退特别适合使用高级模型（如 Claude Opus）时，在其不可用时自动降级到更便宜的模型，确保服务不中断。
      </InfoBox>

      <SectionTitle id="failover-config">相关配置</SectionTitle>
      <CodeBlock language="json5" code={`{
  models: {
    failover: {
      // 是否启用故障转移
      enabled: true,
      // 请求超时后是否触发故障转移
      failOnTimeout: true,
      // 触发故障转移的 HTTP 状态码
      failOnStatusCodes: [429, 500, 502, 503, 504],
      // 最大重试次数（在切换账户前）
      maxRetries: 2,
      // 重试间隔（毫秒）
      retryIntervalMs: 1000,
      // 是否记录故障转移事件
      logEvents: true,
    }
  }
}`} />
    </div>
  );
}
